import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import useMaskedInput from 'hooks/useMaskedInput';
import { BoardMineralization } from 'features/Croptimizer/types';
import { TypeOfKey } from 'utils/types';
import { kgToLb, unMaskInputValue } from 'utils/helpers';
import { Metric } from 'utils/constants';

export const getDefaultSoPrice = (metricValue?: string) => (
  metricValue === Metric.metric
    ? '0,5'
    : unMaskInputValue(String(kgToLb(0.5)))
);

export const resolveStoredSoPrice = (value: string) => {
  const cleaned = value.replace(/\.$/, '');

  if (!cleaned || Number(cleaned) === 0) {
    return '0';
  }

  return unMaskInputValue(cleaned);
};

export const useMineralizationBoard = (_isGerman: boolean) => {
  const { metricValue } = useCroptimizer();

  const {
    mineralizationValues,
    setMineralizationValues,
  } = useCroptimizer();
  const { t } = useTranslation();

  const changeMineralizationValues = useCallback(
    (name: keyof BoardMineralization) => (value: TypeOfKey<BoardMineralization>, clean?: string) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setMineralizationValues((prevValues: BoardMineralization | null) => ({
        ...prevValues,
        [name]: clean !== undefined ? clean : value,
      }));
    },
    [],
  );

  const {
    value: soPrice,
    setValue: setSoPrice,
    clean: cleanSoPrice,
  } = useMaskedInput({
    defaultValue:
            mineralizationValues?.sPrice ?? metricValue === Metric.metric
              ? '0,5'
              : unMaskInputValue(String(kgToLb(0.5))),
    value: unMaskInputValue(
      mineralizationValues?.sPrice ??
            (metricValue === Metric.metric
              ? '0,5'
              : unMaskInputValue(String(kgToLb(0.5)))),
    ),
    onChangeCallback: changeMineralizationValues('sPrice'),
  });

  const { value: month, setValue: setMonth } = useMaskedInput({
    defaultValue: '12',
    value: mineralizationValues?.month ?? '12',
    onChangeCallback: changeMineralizationValues('month'),
  });

  const { value: cap, setValue: setCap } = useMaskedInput({
    defaultValue: t('no'),
    value: mineralizationValues?.cap ?? t('no'),
    onChangeCallback: changeMineralizationValues('cap'),
    isDisableClean: true,
  });

  const {
    value: organicMatter,
    setValue: setOrganicMatter,
    clean: cleanorganicmatter,
  } = useMaskedInput({
    defaultValue: '3',
    value: unMaskInputValue(mineralizationValues?.organicMatter ?? '3'),
    onChangeCallback: changeMineralizationValues('organicMatter'),
  });

  const errorOrganicMatter = useMemo(() => {
    if (Number(cleanorganicmatter) < 0.5) {
      return t('minimumPossibleValue') + ' 0.5';
    }

    if (Number(cleanorganicmatter) > 50) {
      return t('maximumPossibleValue') + ' 50';
    }

    return null;
  }, [organicMatter]);

  const {
    value: phAvg,
    setValue: setPhAvg,
    clean: cleanPhAvg,
  } = useMaskedInput({
    defaultValue: '0,10',
    value: unMaskInputValue(mineralizationValues?.annualDecrease || '0,10'),
    onChangeCallback: changeMineralizationValues('annualDecrease'),
  });

  const errorPhAvg = useMemo(() => {
    if (Number(cleanPhAvg) < 0.01) {
      return t('minimumPossibleValue') + ' 0.01';
    }

    if (Number(cleanPhAvg) > 0.5) {
      return t('maximumPossibleValue') + ' 0.5';
    }

    return null;
  }, [phAvg]);

  const {
    value: soilTillage,
    setValue: setSoilTillage,
    clean: cleansoiltillage,
  } = useMaskedInput({
    defaultValue: '25',
    value: mineralizationValues?.soilTillage ?? '25',
    onChangeCallback: changeMineralizationValues('soilTillage'),
    isDisableClean: true,
  });

  const errorSoilTillage = useMemo(() => {
    if (!cleansoiltillage || cleansoiltillage.trim() === '') {
      return t('minimumPossibleValue') + ' 0';
    }

    if (Number(cleansoiltillage.replace(/[%'.]/g, '')) < 0) {
      return t('minimumPossibleValue') + ' 0';
    }

    if (Number(cleansoiltillage.replace(/[%'.]/g, '')) > 50) {
      return t('maximumPossibleValue') + ' 50';
    }

    return '';
  }, [soilTillage]);

  return {
    cap,
    setCap,
    changeMineralizationValues,
    organicMatter,
    setOrganicMatter,
    cleanorganicmatter,
    errorOrganicMatter,
    phAvg,
    setPhAvg,
    cleanPhAvg,
    errorPhAvg,
    errorSoilTillage,
    setSoilTillage,
    soilTillage,
    month,
    setMonth,
    soPrice,
    setSoPrice,
    cleanSoPrice,
  };
}; 